import { useEffect, useMemo, useState } from 'react';
import type { CallCenterOrder, CallCenterStatus } from '@matix/types';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';
import { useReprintReceipt } from '../print';
import { callCenterApi, ccErrorMessage } from './api';
import { DispatchDialog } from './DispatchDialog';
import { useLiveOrders, useServerClock } from './hooks';
import { OrderCard } from './OrderCard';
import { OrderDetailDrawer } from './OrderDetailDrawer';
import { SettleDialog } from './SettleDialog';
import { useCallCenter } from './store';

/**
 * Does a driver make sense for this order?
 *
 * Order type 2 is delivery — the call centre's default and the only type where
 * somebody carries the bag out. A null is treated as delivery too: it means the
 * tenant never nominated a type, and offering the button on an order that turns
 * out to be a collection is a harmless extra tap, while hiding it on a genuine
 * delivery leaves the cashier with no way to record who took it.
 */
function isDeliverable(order: CallCenterOrder): boolean {
  return order.orderType == null || order.orderType === 2 || order.driverId != null;
}

/**
 * The branch's Pending Orders screen.
 *
 * THIS IS WHERE A CALL-CENTRE ORDER LANDS — not in the cashier's cart. That
 * separation is the whole point of the screen: the till's cart belongs to
 * whoever is standing at the counter right now, and dropping a phone order into
 * it would either interrupt a live sale or be silently discarded by the next
 * "New order". A phone order arrives on its own board, waits there while the
 * kitchen works, and is only touched by the cashier at hand-over.
 *
 * The counter has three things it can do with an order, and they are the three
 * buttons on the card:
 *
 *   * PRINT it — the same slip the till prints, off the same receipt, so the
 *     bag that leaves with a driver carries the document the customer expects.
 *   * SEND IT OUT with a named driver, which records who took it and moves the
 *     order to "out for delivery" in one action.
 *   * TAKE THE PAYMENT, which can happen before the driver leaves or after they
 *     come back with the cash — an order sits unpaid in `out_for_delivery`
 *     quite legitimately, and is settled from this same card when it returns.
 *
 * Everything else on the card is context for those decisions — how long the
 * customer has waited, what they ordered, where it is going.
 *
 * `preparing` and `ready` move on their own: they are written by the kitchen
 * bumping its own tickets and arrive here over the live stream; nobody at the
 * counter types them.
 */
export function PendingOrdersScreen(): JSX.Element {
  const { t } = useLang();
  const bootstrap = useCallCenter((s) => s.bootstrap);
  const [filter, setFilter] = useState<'open' | 'ready' | 'today'>('open');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [settleTarget, setSettleTarget] = useState<CallCenterOrder | null>(null);
  const [dispatchTarget, setDispatchTarget] = useState<CallCenterOrder | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const reprint = useReprintReceipt();

  const params = useMemo(() => {
    if (filter === 'ready') return { status: 'ready,out_for_delivery' };
    if (filter === 'today') return {};
    return { open_only: true };
  }, [filter]);

  const live = useLiveOrders({
    mode: 'branch',
    params,
    pollSeconds: bootstrap?.settings.pollSeconds ?? 15,
  });
  const now = useServerClock(live.serverTime);

  // Local overlay for rows this screen has just changed. The stream and the
  // poll will both deliver the same truth within a second or two, but a button
  // that appears to do nothing until then is a button people press twice.
  const [overrides, setOverrides] = useState<Record<number, CallCenterOrder>>({});
  useEffect(() => {
    setOverrides({});
  }, [filter]);

  const orders = live.orders.map((o) => overrides[o.id] ?? o);

  function applyLocal(order: CallCenterOrder): void {
    setOverrides((prev) => ({ ...prev, [order.id]: order }));
  }

  async function moveTo(order: CallCenterOrder, status: CallCenterStatus): Promise<void> {
    setBusyId(order.id);
    try {
      const updated = await callCenterApi.branch.setStatus(order.id, status);
      applyLocal(updated);
    } catch (err) {
      toast(ccErrorMessage(err, 'Failed to update'), 'error');
    } finally {
      setBusyId(null);
    }
  }

  if (!bootstrap?.provisioned || !bootstrap.settings.isEnabled) {
    return (
      <div className="cc-screen">
        <div className="cc-empty">
        <div className="cc-empty-title">{t({ en: 'Pending orders', ar: 'الطلبات قيد الانتظار' })}</div>
        <p>
          {t({
            en: 'The Call Center is not switched on for this company, so no phone orders arrive here.',
            ar: 'مركز الاتصال غير مفعّل لهذه الشركة، لذا لا تصل طلبات هاتفية هنا.',
          })}
        </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-screen cc-pending">
      <header className="cc-pending-head">
        <div className="cc-tabs">
          {(['open', 'ready', 'today'] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`cc-tab ${filter === f ? 'cc-tab--on' : ''}`}
              onClick={() => setFilter(f)}
            >
              {t(
                {
                  open: { en: 'In progress', ar: 'قيد التنفيذ' },
                  ready: { en: 'Ready to hand over', ar: 'جاهز للتسليم' },
                  today: { en: 'Today', ar: 'اليوم' },
                }[f],
              )}
            </button>
          ))}
        </div>
        <div className="cc-pending-status">
          <span className={`cc-live ${live.live ? 'cc-live--on' : ''}`}>
            {live.live ? t({ en: 'Live', ar: 'مباشر' }) : t({ en: 'Polling', ar: 'تحديث دوري' })}
          </span>
          <button type="button" className="cc-link" onClick={live.refresh}>
            {t({ en: 'Refresh', ar: 'تحديث' })}
          </button>
        </div>
      </header>

      {live.error && <div className="cc-warn">{live.error}</div>}

      {!live.loading && orders.length === 0 && (
        <div className="cc-empty">
          <div className="cc-empty-title">{t({ en: 'Nothing waiting', ar: 'لا شيء في الانتظار' })}</div>
          <p>
            {t({
              en: 'Phone orders for this branch appear here the moment an agent sends them.',
              ar: 'تظهر الطلبات الهاتفية لهذا الفرع هنا فور إرسالها من الوكيل.',
            })}
          </p>
        </div>
      )}

      <div className="cc-grid">
        {orders.map((order) => {
          const busy = busyId === order.id;
          const settleable =
            !order.isPrepaid && order.paymentTypeId == null && order.status !== 'cancelled';

          return (
            <OrderCard
              key={order.id}
              order={order}
              nowMs={now()}
              warnSeconds={live.warnSeconds}
              criticalSeconds={live.criticalSeconds}
              onOpen={() => setDetailId(order.id)}
            >
              {settleable && (
                <button
                  type="button"
                  className="cc-btn cc-btn--primary"
                  disabled={busy}
                  onClick={() => setSettleTarget(order)}
                >
                  {t({ en: 'Take payment', ar: 'تحصيل الدفع' })}
                </button>
              )}

              {/* A prepaid order needs no money, only a hand-over. */}
              {order.isPrepaid && order.status !== 'delivered' && order.status !== 'cancelled' && (
                <button
                  type="button"
                  className="cc-btn cc-btn--primary"
                  disabled={busy}
                  onClick={() => void moveTo(order, 'delivered')}
                >
                  {t({ en: 'Mark delivered', ar: 'تعليم كمُسلّم' })}
                </button>
              )}

              {/*
                Send the order out. Naming a driver is optional inside the
                dialog, so the button says what always happens — the order
                leaves — rather than promising a driver step that a shop where
                whoever is free takes the bag would have to skip.

                Offered right up until the order is delivered, including while
                it is already out, where it becomes "Change driver": the wrong
                name on a bag that has left the building is precisely the thing
                worth being able to correct.
              */}
              {isDeliverable(order) &&
                order.status !== 'delivered' &&
                order.status !== 'cancelled' && (
                  <button
                    type="button"
                    className="cc-btn"
                    disabled={busy}
                    onClick={() => setDispatchTarget(order)}
                  >
                    {order.status === 'out_for_delivery'
                      ? t({ en: 'Change driver', ar: 'تغيير السائق' })
                      : t({ en: 'Send out', ar: 'إرسال للتوصيل' })}
                  </button>
                )}

              <button
                type="button"
                className="cc-btn"
                onClick={() => void reprint(order.receiptId)}
              >
                {t({ en: 'Print', ar: 'طباعة' })}
              </button>

              <button type="button" className="cc-btn" onClick={() => setDetailId(order.id)}>
                {t({ en: 'Details', ar: 'التفاصيل' })}
              </button>
            </OrderCard>
          );
        })}
      </div>

      <SettleDialog
        order={settleTarget}
        paymentTypes={bootstrap.paymentTypes}
        onClose={() => setSettleTarget(null)}
        onSettled={(o) => {
          applyLocal(o);
          live.refresh();
        }}
      />

      <DispatchDialog
        order={dispatchTarget}
        onClose={() => setDispatchTarget(null)}
        onDispatched={(o) => {
          applyLocal(o);
          live.refresh();
        }}
      />

      <OrderDetailDrawer
        orderId={detailId}
        mode="branch"
        onClose={() => setDetailId(null)}
        onChanged={applyLocal}
      />

    </div>
  );
}
